import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { searchHubs } from '@api/hubsApi';

import '../address_autocomplete/autocomplete.css';

/**
 * StationAutocomplete
 * - Cerca fra gli hub del gestore (default: solo `type = station`) via
 *   `/v2/fleet/hubs/search`, che fa prefix search su nome, codice e comune.
 * - Stesso impianto di `AddressAutocomplete`: testo in stato locale (la
 *   digitazione non rerenderizza il parent), fetch debounced con guardia contro
 *   le risposte fuori ordine, dropdown in portal su <body> per non essere
 *   tagliato da card/modali, navigazione tastiera e ARIA combobox/listbox.
 * - `onSelect` emette l'**oggetto stazione completo**: il consumer lo salva
 *   denormalizzato, senza dover rileggere l'hub.
 */
export default function StationAutocomplete({
  value, // stringa (testo) oppure oggetto stazione
  onChange, // (e: { target: { name, value } }) => void — testo digitato
  onSelect, // (station: { id, code, name, city, province, latitude, longitude }) => void
  onBlur,
  onKeyDown,
  placeholder = '',
  label = '',
  id: idProp,
  name,
  type = 'station',
  // Gestore su cui scopare la ricerca. Senza, `searchHubs` ricade sul gestore
  // dell'utente loggato: per un admin MBILITY (che un `gestore` non ce l'ha)
  // il backend risponde "Missing or invalid id_gestore" e la tendina resta
  // muta. Chi conosce il gestore del contesto (l'ordine, il filtro Timeline)
  // deve passarlo.
  idGestore,
  disabled = false,
  readOnly = false,
  minChars = 2,
  debounceMs = 300,
  limit = 20,
}) {
  const autoId = useId();
  const inputId = idProp ?? autoId;
  const listboxId = `${inputId}-listbox`;

  // Il parent può passare sia la stringa digitata sia la stazione selezionata.
  const valueText =
    typeof value === 'object' && value !== null ? (value.name ?? '') : (value ?? '');

  const [inputValue, setInputValue] = useState(valueText);
  // Allineamento allo `value` esterno calcolato durante il render (pattern
  // "adjusting state on prop change"), niente useEffect + setState.
  const [lastValue, setLastValue] = useState(valueText);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);

  const fetchTimerRef = useRef(null);
  const reqIdRef = useRef(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  if (valueText !== lastValue) {
    setLastValue(valueText);
    setInputValue(valueText);
  }

  const runFetch = useCallback(
    (query) => {
      if (fetchTimerRef.current) window.clearTimeout(fetchTimerRef.current);
      fetchTimerRef.current = window.setTimeout(async () => {
        const reqId = ++reqIdRef.current;
        try {
          const rows = await searchHubs({ q: query, type, limit, id_gestore: idGestore });
          if (reqId !== reqIdRef.current) return; // risposta superata
          const list = Array.isArray(rows) ? rows : [];
          setSuggestions(list);
          setOpen(list.length > 0);
          setHighlighted(-1);
        } catch {
          if (reqId !== reqIdRef.current) return;
          setSuggestions([]);
          setOpen(false);
        }
      }, debounceMs);
    },
    [type, limit, debounceMs, idGestore]
  );

  useEffect(
    () => () => {
      if (fetchTimerRef.current) window.clearTimeout(fetchTimerRef.current);
      // Invalida l'eventuale richiesta in volo: al rientro verrà scartata.
      reqIdRef.current += 1;
    },
    []
  );

  useEffect(() => {
    function handleDocMouseDown(e) {
      const inRoot = rootRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inRoot && !inMenu) setOpen(false);
    }
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => document.removeEventListener('mousedown', handleDocMouseDown);
  }, []);

  const updateMenuPosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuStyle({ position: 'fixed', top: r.bottom, left: r.left, width: r.width });
  }, []);

  useLayoutEffect(() => {
    if (open && suggestions.length > 0) updateMenuPosition();
  }, [open, suggestions, inputValue, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onReflow = () => updateMenuPosition();
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open, updateMenuPosition]);

  const handleChange = useCallback(
    (e) => {
      const v = e.target.value;
      setInputValue(v);
      onChange?.({ target: { name, value: v } });
      const q = v.trim();
      if (q.length < minChars) {
        if (fetchTimerRef.current) window.clearTimeout(fetchTimerRef.current);
        setSuggestions([]);
        setOpen(false);
      } else {
        runFetch(q);
      }
    },
    [onChange, name, minChars, runFetch]
  );

  const choose = useCallback(
    (hub) => {
      if (fetchTimerRef.current) window.clearTimeout(fetchTimerRef.current);
      setInputValue(hub?.name ?? '');
      setOpen(false);
      setSuggestions([]);
      setHighlighted(-1);
      onSelect?.({
        id: hub?.id,
        code: hub?.code ?? null,
        name: hub?.name ?? '',
        // `address` serve ai consumer che riportano la stazione nel campo
        // indirizzo del servizio (assistenze a punto singolo).
        address: hub?.address ?? null,
        city: hub?.city ?? null,
        province: hub?.province ?? null,
        latitude: hub?.latitude ?? null,
        longitude: hub?.longitude ?? null,
      });
    },
    [onSelect]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (open && suggestions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlighted((i) => (i + 1) % suggestions.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlighted((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
          return;
        }
        if (e.key === 'Enter' && highlighted >= 0 && highlighted < suggestions.length) {
          e.preventDefault();
          choose(suggestions[highlighted]);
          return;
        }
        if (e.key === 'Escape') {
          setOpen(false);
          return;
        }
      }
      onKeyDown?.(e);
    },
    [open, suggestions, highlighted, choose, onKeyDown]
  );

  const handleBlur = useCallback(
    (e) => {
      setOpen(false);
      onBlur?.(e);
    },
    [onBlur]
  );

  return (
    <div ref={rootRef} className="form-group app-autocomplete">
      {label ? (
        <label htmlFor={inputId} className="app-input__label">
          {label}
        </label>
      ) : null}

      <input
        ref={inputRef}
        id={inputId}
        name={name}
        type="text"
        className="form-control app-input"
        placeholder={placeholder}
        autoComplete="off"
        readOnly={readOnly}
        disabled={disabled}
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={() => suggestions.length && setOpen(true)}
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-expanded={open}
        aria-activedescendant={highlighted >= 0 ? `${inputId}-option-${highlighted}` : undefined}
        role="combobox"
      />

      {open &&
        suggestions.length > 0 &&
        menuStyle &&
        createPortal(
          <ul
            ref={menuRef}
            id={listboxId}
            role="listbox"
            className="app-autocomplete-menu suggestions"
            style={menuStyle}
          >
            {suggestions.map((s, idx) => {
              const isActive = idx === highlighted;
              const place = [s.city, s.province && `(${s.province})`].filter(Boolean).join(' ');
              return (
                <li
                  id={`${inputId}-option-${idx}`}
                  key={s.id ?? `${s.code}-${idx}`}
                  role="option"
                  aria-selected={isActive}
                  className={isActive ? 'is-active' : undefined}
                  onMouseDown={(e) => {
                    // mousedown: il blur dell'input non deve chiudere la lista
                    // prima che il click arrivi.
                    e.preventDefault();
                    choose(s);
                  }}
                >
                  {s.name}
                  {place ? ` — ${place}` : ''}
                  {s.code ? ` · ${s.code}` : ''}
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </div>
  );
}
